import { Language, TourToAppLanguageMap, WEBFLOW_TOUR_LOCALES, webflowLocaleForTourLanguage } from './language';

export type FeaturedTourMediaVariant =
    'desktopBannerMedia' |
    'desktopTitleMedia' |
    'mobileBannerMedia' |
    'mobileTitleMedia';

export class MotesPaths {
    // Motes customer support email
    public static readonly SUPPORT_EMAIL = "support@storyhunt.io";
    public static readonly STUDIO_EMAIL = "studio@storyhunt.io";
    public static readonly SUPPORT_PHONE = "004593103020"; // USED IN HREF LINKS
    public static readonly SUPPORT_PHONE_FORMATTED = "+45 93 10 30 20"; // USED WHEN VISIBLE TO USER
    public static readonly YASSIN_PHONE = "004527515557"; // USED IN HREF LINKS
    public static readonly YASSIN_PHONE_FORMATTED = "+45 27 51 55 57"; // USED WHEN VISIBLE TO USER
    public static readonly YASSIN_EMAIL = "partner@storyhunt.io";

    public static readonly MAPBOX_ACCESS_TOKEN = 'pk.eyJ1Ijoic25hemFtaSIsImEiOiJjaW1pMTBpcDEwMDNydmlrdW8yc2RoY29pIn0.R_8cMvgM26cjwwLW7rlBVQ';
    public static readonly MAPBOX_DEFAULT_STYLE = "mapbox://styles/snazami/cjeo8r9zs4g952rozaiskt0r0";

    public static readonly APPLE_APP_STORE = 'https://apps.apple.com/dk/app/storyhunt/id1563214352';
    public static readonly GOOGLE_PLAY_STORE = 'https://play.google.com/store/apps/details?id=dk.storyhunt.app';

    // public static readonly MOTES_REST = "http://localhost:5000";
    public static readonly MOTES_REST = "https://api.storyhunt.dk";
    public static readonly MOTES_FIREBASE_REST = "https://motesdev.firebaseio.com";
    public static readonly MOTES_HOME = "https://storyhunt.io";
    public static readonly MOTES_HOME_PROFILE = `https://tours.storyhunt.io/profile`;
    public static readonly MOTES_HOME_TOURS = `https://tours.storyhunt.io`;

    public static readonly GO_STORYHUNT = "https://go.storyhunt.io";

    /** Public preview / play link for the new app (tour key only; no previewKey query). */
    public static readonly GO_STORYHUNT_TOUR_URL = (tourKey: string) => `${MotesPaths.GO_STORYHUNT}/tours/${tourKey}`;

    /** Discover deep link for a verified Google place ID (POI). */
    public static readonly GO_STORYHUNT_PLACE_URL = (placeId: string) =>
        `${MotesPaths.GO_STORYHUNT}/?placeId=${encodeURIComponent(placeId)}`;
    public static readonly MOTES_HOME_CREATOR = (lang) => `https://storyhunt.io/${lang}/creator`;
    public static readonly MOTES_HOME_CONTACT = (lang) => `https://storyhunt.io/${lang}/contact`;
    public static readonly MOTES_APP = "https://app.storyhunt.dk";
    public static readonly MOTES_DOCS = "https://docs.storyhunt.dk";
    public static readonly MOTES_API_DOCS = "https://api.storyhunt.dk/v1/docs";
    public static readonly MOTES_API_DREAMHACK_TOKEN = MotesPaths.MOTES_REST + "/api/dreamhack-access-token";
    public static readonly MOTES_API_OIDC_TOKEN = MotesPaths.MOTES_REST + "/api/oidc-access-token";
    public static readonly MOTES_DEV_SERVERS = ["https://dev.motes.dk", "https://dev.storyhunt.dk", "http://localhost", "https://localhost", "https://127.0.0.1"];

    /**
     * Get Webflow tour URL based on slug and language.
     * @param slug The Webflow tour slug
     * @param language The tour language
     * @returns The full Webflow tour URL
     */
    public static readonly WEBFLOW_TOUR_URL = (slug: string, language: Language | string) => {
        const locale = webflowLocaleForTourLanguage(language);
        return `${WEBFLOW_TOUR_LOCALES[locale].baseUrl}${slug}`;
    };

    // Public tours info page to tours (public redirect or sub domain url)
    public static readonly HOME_TOUR_PAGE = (tour: { key: string, language: string; }, publicUrl: boolean = true) => {
        const appLanguage = TourToAppLanguageMap[tour.language] || Language.en;
        // Use the public url (that will be redirected) or the tours subdomain url
        const base = publicUrl ? MotesPaths.MOTES_HOME : MotesPaths.MOTES_HOME_TOURS;
        return `${base}/${appLanguage}/tours/${tour.key}`;
    };

    public static readonly CREATOR_TOUR_PREVIEW_LINK = (tour: { key: string, language: string, previewKey: string; }, publicUrl: boolean = true) => {
        return `${MotesPaths.HOME_TOUR_PAGE(tour, publicUrl)}?previewKey=${tour.previewKey}`;
    };

    public static readonly CREATOR_TOUR_SHARE_LINK = MotesPaths.HOME_TOUR_PAGE;

    public static readonly CREATOR_URL = "https://creator.storyhunt.io";

    public static readonly CREATOR_PROJECT_INVITE_URL = (projectKey: string, pendingMemberKey: string) => {
        return `${MotesPaths.CREATOR_URL}/dashboard/${projectKey}/overview?invited=${pendingMemberKey}`;
    };

    public static readonly CREATOR_EMAIL_VERIFICATION_URL = (verificationToken: string) => {
        return `${MotesPaths.CREATOR_URL}/login?verification=${verificationToken}`;
    };

    // Map paths
    public static readonly STORY_MAP = (tourKey = "") => `tours/${tourKey}/map`;
    public static readonly TOUR_MAP = `tours/map`;

    // Project paths
    public static readonly PROJECT_DB_PATH = (projectKey = "") => `projects/${projectKey}`;
    public static readonly PROJECT_DB_OWNERS = (projectKey = "") => `projects/${projectKey}/owners`;
    public static readonly PROJECT_DB_OWNER = (projectKey = "") => `projects/${projectKey}/owner`;
    public static readonly DEFAULT_PROJECT_KEY = 'default';
    public static readonly DEFAULT_TOUR_KEY = 'default_tour';

    // User paths
    public static readonly USER_REST_API = (userKey, action) => `${MotesPaths.MOTES_REST}/v1/users/${userKey}/actions/${action}`;
    public static readonly USER_PUBLIC_DB_PATH = (userKey = "") => `users/public/${userKey}`;
    public static readonly USER_PRIVATE_DB_PATH = (userKey = "") => `users/private/${userKey}`;
    public static readonly USER_CHALLENGE_STATE_DB_PATH = (userKey: string, challengeKey: string) => `users/public/${userKey}/challengeStateMap/${challengeKey}`;
    public static readonly USER_CHALLENGE_LINK_DB_PATH = () => `userChallengeLinks/`;
    public static readonly USER_PUBLIC_MEDIA_DB_PATH = (userKey: string) => `users/public/${userKey}/profilePic`;
    public static readonly USER_PUBLIC_MEDIA_HD_PATH = (userKey: string) => `users/${userKey}/profile-media`;

    /** Creator TTS terms acceptance — keyed by uid; stores userName + email for lookup */
    public static readonly TTS_AUDIO_OWNERSHIP_ACCEPTANCE_DB_PATH = (userKey = "") => `creatorFeatureAcceptances/ttsAudioOwnership/${userKey}`;

    /** Creator publish content-responsibility acceptance — keyed by uid */
    public static readonly PUBLISH_CONTENT_ACCEPTANCE_DB_PATH = (userKey = "") => `creatorFeatureAcceptances/publishContentResponsibility/${userKey}`;

    public static readonly ADMIN_REQ_PUBLIC_DB_PATH = (userKey = "") => `/adminRequests/public/${userKey}`;
    public static readonly ADMIN_REQ_PRIVATE_DB_PATH = (userKey = "") => `/adminRequests/private/${userKey}`;

    // Group paths
    public static readonly GROUP_DB_PATH = (groupKey = "") => `groups/${groupKey}`;
    public static readonly GROUP_EVENTS_DB_PATH = (groupKey, eventKey = "") => `groups/${groupKey}/events/${eventKey}`;
    public static readonly GROUP_MEMBER_DB_PATH = (groupKey, userKey) => `groups/${groupKey}/members/${userKey}`;
    public static readonly GROUP_MEMBER_STATE_DB_PATH = (groupKey, userKey) => `groups/${groupKey}/members/${userKey}/state`;
    public static readonly GROUP_MEMBER_CHALLENGE_STATE_DB_PATH = (groupKey, userKey, challengeKey) => `groups/${groupKey}/members/${userKey}/challengeStateMap/${challengeKey}`;

    // Mote paths
    public static readonly MOTE_DB_PATH = (moteKey = "") => `motes/${moteKey}`;
    public static readonly MOTE_MEDIA_DB_PATH = (moteKey, idx) => `motes/${moteKey}/content/media/${idx}`;
    public static readonly PARTNER_TRANSFERS_DB_PATH = (transferKey = "") => `partnerTransfers/${transferKey}`;
    public static readonly MOTE_MEDIA_HD_PATH = (projectKey) => `media/${projectKey || 'unknown_project'}`;
    public static readonly MOTE_MEDIA_STORAGE_PATH = (projectKey, mediaKey, fileType, ext) => `${MotesPaths.MOTE_MEDIA_HD_PATH(projectKey)}/${mediaKey}_${fileType}.${ext}`;
    // public static readonly MOTE_MEDIA_HD_PATH = (projectKey) => `tmp-media-test/${projectKey || 'unknown_project'}`;

    // Media paths
    public static readonly MEDIA_LIB_DB_PATH = (mediaKey = "") => `media/${mediaKey}`;
    public static readonly MEDIA_LIB_HD_PATH = (projectKey = "") => `media/${projectKey}`;
    public static readonly MEDIA_LINK_DB_PATH = (mediaKey = "") => `mediaLinks/${mediaKey}`;

    public static readonly GENERATE_SCREENSHOT_ENDPOINT = (mediaKey) => `${MotesPaths.MOTES_REST}/v1/media/${mediaKey}/actions/generate-video-placeholder`;

    // Quiz / challenge paths
    public static readonly CHALLENGE_DB_PATH = (challengeKey = "") => `challenges/${challengeKey}`;

    // Voucher / payment
    public static readonly VOUCHER_REST_API = (action = "") => `${MotesPaths.MOTES_REST}/v1/vouchers` + (action ? `/actions/${action}` : '');
    public static readonly VOUCHER_DB_PATH = (voucherKey = "") => `vouchers/${voucherKey}`;
    public static readonly PAYMENT_DB_PATH = (paymentKey = "") => `payment/${paymentKey}`;

    // Barcodes / tickets to partners
    public static readonly BARCODE_DB_PATH = (partnerProductId = "", barcodeKey = "") => `barcodes/${partnerProductId}/${barcodeKey}`;
    public static readonly DEFAULT_BARCODE_WEBHOOK = `${MotesPaths.MOTES_REST}/v1/barcodes/allocate`;

    // Home voucher pages
    public static readonly VOUCHER_RECEIPT_PAGE = (poolKey: string, tourLang: Language) => {
        const homeLang = TourToAppLanguageMap[tourLang] || Language.en;
        return `/${homeLang}/purchase-confirmation/${poolKey}`;
    };
    public static readonly VOUCHER_RECEIPT_PAGE_REDIRECT = (voucherKeys: string, tourLang: Language) => {
        const homeLang = TourToAppLanguageMap[tourLang] || Language.en;
        return `/${homeLang}/purchase-confirmation?vouchers=${voucherKeys}`;
    };
    public static readonly OLD_VOUCHER_LINK = (voucherKeys: string, tourLang: Language) => {
        const homeLang = TourToAppLanguageMap[tourLang] || Language.en;
        return `/${homeLang}/profile/my-vouchers?vouchers=${voucherKeys}`;
    };

    // Product paths
    public static readonly PRODUCT_DB_PATH = (productKey = "") => `products/${productKey}`;
    public static readonly PRODUCT_HD_PATH = (projectKey, productKey) => `${MotesPaths.WORLD_MEDIA_HD_PATH(projectKey)}/products/${productKey}`;
    public static readonly PRODUCT_MEDIA_DB_PATH = (productKey, mediaIdx) => `${MotesPaths.PRODUCT_DB_PATH(productKey)}/mediaArray/${mediaIdx}`;

    public static readonly DISCOUNT_DB_PATH = (discountKey = "") => `discounts/${discountKey}`;
    public static readonly START_TOUR = (tourKey = "") => `/tours/${tourKey}/start`;
    public static readonly HOME_START_TOUR = (tour: { key: string, language: string; }, publicUrl) => `${MotesPaths.HOME_TOUR_PAGE(tour, publicUrl)}?startTour=true`;
    // App urls
    public static readonly APP_CLAIM_PAGE = (poolKey = "") => `claim-tour/${poolKey}`;
    public static readonly APP_SHARE_PAGE = (poolKey = "") => `share-tour/${poolKey}`;
    public static readonly APP_CLAIM_URL = (poolKey = "") => `${MotesPaths.MOTES_APP}/${MotesPaths.APP_CLAIM_PAGE(poolKey)}`;
    public static readonly APP_SHARE_URL = (poolKey = "") => `${MotesPaths.MOTES_APP}/${MotesPaths.APP_SHARE_PAGE(poolKey)}`;

    // Access rights for StoryHunt API and app integration
    public static readonly ACCESS_RIGHT_DB_PATH = (apiKey = "") => `accessRights/${apiKey}`;

    // Predefined location
    public static readonly VENUE_DB_PATH = (venueKey = "") => `venues/${venueKey}`;

    // Featured Tours
    public static readonly FEATURED_DB_PATH = (key = "") => `featuredTours/${key}`;
    public static readonly FEATURED_MEDIA_DB_PATH = (tourKey, variant: FeaturedTourMediaVariant) => `featuredTours/${tourKey}/${variant}`;
    public static readonly FEATURED_MEDIA_HD_PATH = (tourKey = `unknown_${Date.now()}`) => `featuredTours/${tourKey}`;

    // Blog post paths
    public static readonly POST_DB_PATH = (postKey = "") => `posts/${postKey}`;
    public static readonly POST_MEDIA_DB_PATH = (postKey) => `posts/${postKey}/image`;
    public static readonly POST_HD_PATH = () => `blog/`;

    // StoryHunt Home
    public static readonly PAGE_META_DB_PATH = (pageKey = "") => `pages/${pageKey}`;
    public static readonly TOUR_INFO_DB_PATH = (tourKey = "") => `tourInfo/${tourKey}`;

    // Static assets
    public static readonly HD_PLACEHOLDER = "https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/static-images%2Fplaceholders%2Fhd.jpeg?alt=media&token=cbf2b150-b814-40d0-b1c9-c94d0114cf7a";
    public static readonly PREVIEW_PLACEHOLDER = "https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/static-images%2Fplaceholders%2Fhd.jpeg?alt=media&token=cbf2b150-b814-40d0-b1c9-c94d0114cf7a";
    public static readonly THUMB_PLACEHOLDER = "https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/static-images%2Fplaceholders%2Fthumb.jpeg?alt=media&token=fb38f942-1dfa-4629-b44e-fd15c3f271b6";
    public static readonly SQUARE_PLACEHOLDER = "https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/static-images%2Fplaceholders%2Fhd.jpeg?alt=media&token=cbf2b150-b814-40d0-b1c9-c94d0114cf7a";

    // Profile assets
    public static readonly PROFILE_THUMB_PLACEHOLDER = "https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/media%2Fdefault%2F-MuBvnfT8myqSSFqN-yQ_thumb?alt=media&token=5df4dd7b-ce6c-4bcb-8472-7e24248d89e8";

    // Local storage paths
    public static readonly LOCAL_STORAGE_FIRST_TOUR_FLOW_SEEN = `firstFlowSeen`;
    public static readonly LOCAL_STORAGE_WELCOME_SEEN = (tourKey = "") => `welcomeSeen/${tourKey}`;
    public static readonly LOCAL_STORAGE_COOKIES_ACCEPTED = 'cookiesAccepted';
    public static readonly LOCAL_STORAGE_DIRECTION_ALLOWED = 'directionAllowed';
    public static readonly LOCAL_STORAGE_LOCATION_IGNORED = (tourKey = "") => `locationChosen/${tourKey}`;
    public static readonly LOCAL_STORAGE_GROUP_PRES_SEEN = (tourKey = "") => `groupPresentationSeen/${tourKey}`;
    public static readonly LOCAL_STORAGE_GROUP_INFO_SEEN = 'groupInfoSeen';
    public static readonly LOCAL_STORAGE_CHALLENGE_SEEN = (tourKey = "") => `challengeSeen/${tourKey}`;
    public static readonly LOCAL_STORAGE_TRAILER_SEEN = (tourKey = "") => `trailerSeen/${tourKey}`;
    public static readonly LOCAL_STORAGE_MAP_INTRO_SEEN = (tourKey = "") => `mapIntroSeen/${tourKey}`;
    public static readonly LOCAL_STORAGE_FLOW_COMPLETED = (tourKey = MotesPaths.DEFAULT_TOUR_KEY) => `flowCompleted/${tourKey}`;
    public static readonly LOCAL_STORAGE_ACTIVE_PROJECT = `activeProject`;

    public static readonly DREAM_HACK_AUTH_URL = "https://api.dreamhack.com/1/identity/me";

    // SendInBlue email templates
    public static readonly CREATOR_WELCOME = 34;
    public static readonly WORLD_INVITE = 33;
    public static readonly USER_VERIFICATION = 35;
    public static readonly CREATOR_UPGRADE_REQUEST = 37;
    public static readonly VOUCHER_RECEIPT_EN = 39;
    public static readonly VOUCHER_RECEIPT_DA = 41;

    // World paths
    public static readonly WORLD_MEDIA_DB_PATH = (worldKey) => `projects/${worldKey}/image`;
    public static readonly WORLD_MEDIA_HD_PATH = (worldKey) => `media/worlds/${worldKey}`;
    public static readonly WORLD_OWNERSHIP_LIMIT = 100;

    // Sessions paths
    public static readonly SESSIONS_DB_PATH = (sessionKey) => `sessions/${sessionKey}`;

    // Native app
    public static readonly NATIVE_APP_ID = "dk.storyhunt.app";
    public static readonly NATIVE_APP_URL = "storyhunt://";
}
